import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment-timezone";
import {
  createAppointment,
  updateAppointment,
  selectCalendarLoading,
  clearErrors,
} from "../slice";
import CustomToast from "../../../components/common/CustomToast";
import AppointmentSection from "./AppointmentSection";
import AddressSection from "./AddressSection";
import PropertySection from "./PropertySection";
import ClientSection from "./ClientSection";
import AgentSection from "./AgentSection";
import JobSetupModal from "./JobSetupModal";
import { usePropertyAutoFill } from "../hooks/usePropertyAutoFill";
import { useAgentSearch } from "../hooks/useAgentSearch";
import { validateEmail } from "../../../../utils/helperFunctions/inputValidations";
import {
  isDataNull,
  parseIntOrNull,
} from "../../../../utils/helperFunctions/valueUtils";
import "./AppointmentModal.css";

// Helper functions for agent role mapping
const mapAgentRole = (role) => {
  if (!role) return "";

  const roleLower = role.toLowerCase();
  if (roleLower.includes("buyer")) {
    return "Buyer's Agent";
  } else if (roleLower.includes("seller")) {
    return "Seller's Agent";
  } else if (role === "Buyer's Agent" || role === "Seller's Agent") {
    return role; // Exact match
  } else {
    return "Others";
  }
};

const getSpecifyAgentRole = (role) => {
  if (!role) return "";

  const roleLower = role.toLowerCase();
  // If it's not one of our standard roles, put it in specifyAgentRole
  if (
    !roleLower.includes("buyer") &&
    !roleLower.includes("seller") &&
    role !== "Buyer's Agent" &&
    role !== "Seller's Agent"
  ) {
    return role;
  }
  return "";
};

const isClientNull = (client) => {
  // Client marked N/A after creation may still be an object (with id); treat as null when name and email are both null.
  return (
    isDataNull(client) || (client?.name === null && client?.email === null)
  );
};

const getClientFields = (formData) => {
  if (formData.clientNotApplicable) {
    return {
      client_name: null,
      client_phone: null,
      client_email: null,
      client_note: null,
    };
  }
  return {
    client_name: formData.clientName,
    client_phone: formData.clientPhone || "",
    client_email: formData.clientEmail,
    client_note: formData.clientNote || "",
  };
};

const getAgentValue = (formData) => {
  if (formData.agentNotApplicable) return null;
  return formData.agentId || formData.agent || null;
};

const getAgentRoleValue = (formData) => {
  if (formData.agentNotApplicable || !formData.agentRole) return "";
  if (formData.agentRole === "Others") return formData.specifyAgentRole;
  return formData.agentRole;
};

const AppointmentModal = ({ show, onHide, appointment, initialSlot }) => {
  const dispatch = useDispatch();
  const loading = useSelector(selectCalendarLoading);

  // Form state
  const [formData, setFormData] = useState(() => {
    const now = moment();
    return {
      date: now.format("YYYY-MM-DD"),
      startTime: "09:00",
      endTime: "10:00",
      duration: "60",
      address1: "",
      address2: "",
      city: "",
      state: "",
      postalCode: "",
      note: "",
      year_build: "",
      floor_area: "",
      stories: "",
      house_type: "",
      apn: "",
      info: "",
      clientName: "",
      clientPhone: "",
      clientEmail: "",
      clientNote: "",
      agent: "",
      agentId: "",
      agentRole: "",
      specifyAgentRole: "",
      clientNotApplicable: false,
      agentNotApplicable: false,
    };
  });

  // Error state
  const [formErrors, setFormErrors] = useState({});

  // Toast state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("success");

  // Job Setup Modal state
  const [showJobSetupModal, setShowJobSetupModal] = useState(false);
  const [savedAppointment, setSavedAppointment] = useState(null);

  // Check if we're in edit mode
  const isEditMode = !!appointment;

  // Initialize form with passed data
  useEffect(() => {
    if (show) {
      // Clear Redux errors when modal opens
      dispatch(clearErrors());

      const now = moment();
      let initialFormData;

      if (appointment) {
        // Pre-populate form with editing appointment data
        const startTime = moment(
          appointment.start_time.substring(0, 19)
        ).format("HH:mm");
        const endTime = moment(appointment.end_time.substring(0, 19)).format(
          "HH:mm"
        );
        const duration = moment(appointment.end_time.substring(0, 19)).diff(
          moment(appointment.start_time.substring(0, 19)),
          "minutes"
        );

        // Extract address data from job report
        let address1 = "",
          address2 = "",
          city = "",
          state = "",
          postalCode = "";

        const addressObj = appointment.job?.report?.address;
        if (addressObj && typeof addressObj === "object") {
          address1 = addressObj.address_line1 || "";
          address2 = addressObj.address_line2 || "";
          city = addressObj.city || "";
          state = addressObj.state || "";
          postalCode = addressObj.zip_code || "";
        }

        const isClientNA = isClientNull(appointment.job?.report?.client);

        const isAgentNA = appointment.job?.report?.agent_applicable === false;

        initialFormData = {
          date: moment(appointment.date).format("YYYY-MM-DD"),
          startTime: startTime,
          endTime: endTime,
          duration: duration.toString(),
          address1: address1,
          address2: address2,
          city: city,
          state: state,
          postalCode: postalCode,
          note: appointment.note || "",
          year_build: appointment.job?.report?.year_build || "",
          floor_area: appointment.job?.report?.floor_area || "",
          stories: appointment.job?.report?.stories || "",
          house_type: appointment.job?.report?.house_type || "",
          apn: appointment.job?.report?.apn || "",
          info: appointment.job?.report?.info || "",
          clientName: appointment.job?.report?.client?.name || "",
          clientPhone: appointment.job?.report?.client?.phone || "",
          clientEmail: appointment.job?.report?.client?.email || "",
          clientNote: appointment.job?.report?.client?.note || "",
          agent: appointment.job?.report?.agent?.name || "",
          agentId: appointment.job?.report?.agent?.id || "",
          agentRole: mapAgentRole(appointment.job?.report?.agent?.role || ""),
          specifyAgentRole: getSpecifyAgentRole(
            appointment.job?.report?.agent?.role || ""
          ),
          clientNotApplicable: isClientNA,
          agentNotApplicable: isAgentNA,
        };
      } else if (initialSlot) {
        // Create mode with initial time slot (from calendar selection)
        const start = moment(initialSlot.start);
        initialFormData = {
          date: start.format("YYYY-MM-DD"),
          startTime: "09:00",
          endTime: "10:00",
          duration: 60, // At least 1 hour
          address1: "",
          address2: "",
          city: "",
          state: "",
          postalCode: "",
          note: "",
          year_build: "",
          floor_area: "",
          stories: "",
          house_type: "",
          apn: "",
          info: "",
          clientName: "",
          clientPhone: "",
          clientEmail: "",
          clientNote: "",
          agent: "",
          agentId: "",
          agentRole: "",
          specifyAgentRole: "",
          clientNotApplicable: false,
          agentNotApplicable: false,
        };
      } else {
        // Default form data for create mode (no initial slot)
        initialFormData = {
          date: now.format("YYYY-MM-DD"),
          startTime: "09:00",
          endTime: "10:00",
          duration: "60",
          address1: "",
          address2: "",
          city: "",
          state: "",
          postalCode: "",
          note: "",
          year_build: "",
          floor_area: "",
          stories: "",
          house_type: "",
          apn: "",
          info: "",
          clientName: "",
          clientPhone: "",
          clientEmail: "",
          clientNote: "",
          agent: "",
          agentId: "",
          agentRole: "",
          specifyAgentRole: "",
          clientNotApplicable: false,
          agentNotApplicable: false,
        };
      }

      setFormData(initialFormData);
      setFormErrors({});
    }
  }, [show, appointment, initialSlot, dispatch, isEditMode]);

  // Helper functions for time calculations
  const calculateEndTime = (startTime, duration) => {
    if (!startTime || !duration) return "";

    const [hours, minutes] = startTime.split(":").map(Number);
    const startMinutes = hours * 60 + minutes;
    const endMinutes = startMinutes + parseInt(duration);

    // Handle midnight crossover
    const endHours = Math.floor((endMinutes % (24 * 60)) / 60);
    const endMins = (endMinutes % (24 * 60)) % 60;

    return `${endHours.toString().padStart(2, "0")}:${endMins.toString().padStart(2, "0")}`;
  };

  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return "";

    const [startHours, startMinutes] = startTime.split(":").map(Number);
    const [endHours, endMinutes] = endTime.split(":").map(Number);

    const startTotalMinutes = startHours * 60 + startMinutes;
    let endTotalMinutes = endHours * 60 + endMinutes;

    // Handle midnight crossover
    if (endTotalMinutes < startTotalMinutes) {
      endTotalMinutes += 24 * 60;
    }

    const duration = endTotalMinutes - startTotalMinutes;
    return duration > 0 ? duration.toString() : "";
  };

  const handleInputChange = (field, value) => {
    let updatedData = { [field]: value };

    // Synchronize time fields based on which field was changed
    if (field === "startTime") {
      // When start time changes, update end time based on current duration
      if (formData.duration) {
        updatedData.endTime = calculateEndTime(value, formData.duration);
      }
    } else if (field === "endTime") {
      // When end time changes, check if it's before start time and handle accordingly
      if (formData.startTime) {
        // Check if endTime is before startTime (excluding midnight crossover)
        const [startHours, startMins] = formData.startTime
          .split(":")
          .map(Number);
        const [endHours, endMins] = value.split(":").map(Number);
        const startTotal = startHours * 60 + startMins;
        const endTotal = endHours * 60 + endMins;

        if (endTotal < startTotal) {
          // EndTime is earlier than startTime - adjust startTime to 1 hour before endTime
          const newStartTotal = endTotal - 60;
          if (newStartTotal >= 0) {
            // Normal case: new start time is still today
            const newStartHours = Math.floor(newStartTotal / 60);
            const newStartMins = newStartTotal % 60;
            updatedData.startTime = `${newStartHours.toString().padStart(2, "0")}:${newStartMins.toString().padStart(2, "0")}`;
            updatedData.duration = "60";
          } else {
            // Edge case: would push start time to previous day, so keep current startTime
            // and calculate duration assuming endTime is next day
            const newDuration = calculateDuration(formData.startTime, value);
            if (newDuration) {
              updatedData.duration = newDuration;
            }
          }
        } else {
          // Normal case: endTime is after startTime
          const newDuration = calculateDuration(formData.startTime, value);
          if (newDuration) {
            updatedData.duration = newDuration;
          }
        }
      }
    } else if (field === "duration") {
      // When duration changes, update end time based on start time
      if (formData.startTime) {
        updatedData.endTime = calculateEndTime(formData.startTime, value);
      }
    }

    setFormData((prev) => ({
      ...prev,
      ...updatedData,
    }));

    // Clear errors for updated fields
    const fieldsToCheck = [field, ...Object.keys(updatedData)];
    const clearedErrors = {};
    fieldsToCheck.forEach((fieldName) => {
      if (formErrors[fieldName]) {
        clearedErrors[fieldName] = null;
      }
    });

    if (Object.keys(clearedErrors).length > 0) {
      setFormErrors((prev) => ({
        ...prev,
        ...clearedErrors,
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    // Required fields
    if (!formData.date) {
      errors.date = "Date is required";
    }

    if (!formData.startTime) {
      errors.startTime = "Start time is required";
    }

    if (!formData.endTime) {
      errors.endTime = "End time is required";
    }

    if (!formData.duration || parseInt(formData.duration) <= 0) {
      errors.duration = "Duration must be greater than 0";
    }

    // Validate that end time is after start time
    if (formData.startTime && formData.endTime) {
      const duration = calculateDuration(formData.startTime, formData.endTime);
      if (!duration || parseInt(duration) <= 0) {
        errors.endTime = "End time must be after start time";
      }
    }

    if (!formData.address1) {
      errors.address1 = "Address1 is required";
    }

    if (!formData.city) {
      errors.city = "City is required";
    }

    if (!formData.state) {
      errors.state = "State is required";
    }

    if (!formData.postalCode) {
      errors.postalCode = "Postal code is required";
    }

    // Client validation - only if not marked as N/A
    if (!formData.clientNotApplicable) {
      if (!formData.clientName) {
        errors.clientName = "Client name is required";
      }

      if (!formData.clientEmail) {
        errors.clientEmail = "Client email is required";
      }

      // Email format validation
      if (formData.clientEmail && !validateEmail(formData.clientEmail)) {
        errors.clientEmail = "Please enter a valid email address";
      }
    }

    // Agent validation - only if not marked as N/A
    if (!formData.agentNotApplicable) {
      if (!formData.agent) {
        errors.agent = "Agent is required";
      }

      if (!formData.agentRole) {
        errors.agentRole = "Agent role is required";
      }

      if (formData.agentRole === "Others" && !formData.specifyAgentRole) {
        errors.specifyAgentRole = "Please specify the agent role";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e, openJobSetup = false) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Calculate start and end times in ISO format using the actual endTime field
      const startDateTime = moment(
        `${formData.date} ${formData.startTime}`,
        "YYYY-MM-DD HH:mm"
      );
      const endDateTime = moment(
        `${formData.date} ${formData.endTime}`,
        "YYYY-MM-DD HH:mm"
      );

      // Handle midnight crossover - if end time is before start time, it's next day
      if (endDateTime.isBefore(startDateTime)) {
        endDateTime.add(1, "day");
      }

      // Prepare appointment data
      const appointmentData = {
        start_time: startDateTime.format("YYYY-MM-DDTHH:mm:ss"),
        end_time: endDateTime.format("YYYY-MM-DDTHH:mm:ss"),
        note: formData.note || "",

        // Address fields
        address_line1: formData.address1,
        address_line2: formData.address2 || "",
        city: formData.city,
        state: formData.state,
        zip_code: formData.postalCode,

        // Property details
        year_build: parseIntOrNull(formData.year_build),
        floor_area: parseIntOrNull(formData.floor_area),
        stories: parseIntOrNull(formData.stories),
        house_type: formData.house_type || "",
        apn: formData.apn || "",
        info: formData.info || "",

        // Client details - send null if marked as Not Applicable
        client_applicable: !formData.clientNotApplicable,
        ...getClientFields(formData),

        // Agent - send null if marked as Not Applicable
        agent_applicable: !formData.agentNotApplicable,
        agent: getAgentValue(formData),
        agent_role: getAgentRoleValue(formData),
      };

      let result;
      if (isEditMode && appointment) {
        // Update existing appointment
        result = await dispatch(
          updateAppointment({
            id: appointment.id,
            ...appointmentData,
          })
        ).unwrap();
        setToastMessage("Job Booking updated successfully.");
      } else {
        // Create new appointment
        result = await dispatch(createAppointment(appointmentData)).unwrap();
        setToastMessage("Job Booking saved successfully.");
      }

      // Show success toast
      setToastVariant("success");
      setShowToast(true);

      if (openJobSetup) {
        // Store the saved appointment data for JobSetupModal
        setSavedAppointment(result);
        setShowJobSetupModal(true);
        // Close appointment modal when job setup opens
        onHide();
      } else {
        // Just close the modal
        onHide();
      }
    } catch (error) {
      console.error(
        `Failed to ${isEditMode ? "update" : "create"} appointment:`,
        error
      );
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        name: error.name,
      });

      // Show error toast but keep modal open
      setToastMessage(
        `Failed to ${isEditMode ? "update" : "save"} Job Booking. Please try again.`
      );
      setToastVariant("error");
      setShowToast(true);
    }
  };

  // Initialize property auto-fill hook for address section
  const { handleAutoAutoFill, autoFillLoading, handleManualAutoFill } =
    usePropertyAutoFill();

  // Get agent search hook - lift to parent to share state
  const {
    agentOptions,
    showAgentDropdown,
    loadingAgents,
    selectedAgentIndex,
    agentInputRef,
    dropdownRef,
    fetchDefaultAgents,
    handleAgentFocus,
    handleAgentKeyDown,
    handleAgentSelect,
    handleAgentChange,
    handleClearAgent,
    setSelectedAgentIndex,
  } = useAgentSearch();

  const handleAddressBlur = () => {
    handleAutoAutoFill(
      formData,
      setToastMessage,
      setToastVariant,
      setShowToast,
      handleInputChange
    );
  };

  const handleClose = () => {
    setFormErrors({});
    setShowJobSetupModal(false);
    setSavedAppointment(null);
    onHide();
  };

  const handleJobSetupClose = () => {
    setShowJobSetupModal(false);
    setSavedAppointment(null);
    // Don't close appointment modal since it's already closed
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size="lg"
        centered
        className="appointment-modal"
        backdrop="static"
      >
        <Modal.Header className="appointment-modal-header">
          <Modal.Title className="appointment-modal-title">
            {isEditMode ? "Edit Job Booking" : "Add Job Booking"}
          </Modal.Title>
          <Button
            variant="link"
            className="appointment-modal-close"
            onClick={handleClose}
          >
            <i
              className="bi bi-x"
              style={{ fontSize: "16px", opacity: 0.5 }}
            ></i>
          </Button>
        </Modal.Header>

        <Modal.Body className="appointment-modal-content">
          <Form onSubmit={handleSubmit}>
            <AppointmentSection
              formData={formData}
              formErrors={formErrors}
              onInputChange={handleInputChange}
            />

            <AddressSection
              formData={formData}
              formErrors={formErrors}
              onInputChange={handleInputChange}
              onAddressBlur={handleAddressBlur}
            />

            <PropertySection
              formData={formData}
              formErrors={formErrors}
              onInputChange={handleInputChange}
              setToastMessage={setToastMessage}
              setToastVariant={setToastVariant}
              setShowToast={setShowToast}
              autoFillLoading={autoFillLoading}
              handleManualAutoFill={handleManualAutoFill}
              handleAutoAutoFill={handleAutoAutoFill}
            />

            <ClientSection
              formData={formData}
              formErrors={formErrors}
              onInputChange={handleInputChange}
              isNotApplicable={formData.clientNotApplicable}
            />

            {/* Client-Provided Info (read-only, edit mode only) */}
            {isEditMode && appointment?.note && (
              <div className="appointment-section">
                <div className="client-provided-card client-provided-notes">
                  <div className="client-provided-card-title">
                    Client-Provided Notes
                  </div>
                  <div className="client-provided-card-content">
                    {appointment.note}
                  </div>
                </div>
              </div>
            )}

            {isEditMode &&
              (appointment?.job?.client_provided_agent_name ||
                appointment?.job?.client_provided_agent_phone ||
                appointment?.job?.client_provided_agent_email) && (
                <div className="appointment-section">
                  <div className="client-provided-card client-provided-agent">
                    <div className="client-provided-card-title">
                      Client-Provided Agent
                    </div>
                    {appointment.job?.client_provided_agent_name && (
                      <div className="client-provided-agent-name">
                        {appointment.job.client_provided_agent_name}
                      </div>
                    )}
                    <div className="client-provided-agent-details">
                      {appointment.job?.client_provided_agent_phone && (
                        <span>
                          {appointment.job.client_provided_agent_phone}
                        </span>
                      )}
                      {appointment.job?.client_provided_agent_email && (
                        <span>
                          {appointment.job.client_provided_agent_email}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

            <AgentSection
              formData={formData}
              formErrors={formErrors}
              onInputChange={handleInputChange}
              isNotApplicable={formData.agentNotApplicable}
              agentOptions={agentOptions}
              showAgentDropdown={showAgentDropdown}
              loadingAgents={loadingAgents}
              selectedAgentIndex={selectedAgentIndex}
              agentInputRef={agentInputRef}
              dropdownRef={dropdownRef}
              fetchDefaultAgents={fetchDefaultAgents}
              handleAgentFocus={handleAgentFocus}
              handleAgentKeyDown={handleAgentKeyDown}
              handleAgentSelect={handleAgentSelect}
              handleAgentChange={handleAgentChange}
              handleClearAgent={handleClearAgent}
              setSelectedAgentIndex={setSelectedAgentIndex}
            />
          </Form>
        </Modal.Body>

        <Modal.Footer className="appointment-modal-footer">
          <Button variant="outline-secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="outline-primary"
            onClick={(e) => handleSubmit(e, false)}
            disabled={
              loading.create ||
              loading.update ||
              autoFillLoading ||
              loadingAgents
            }
          >
            Save
          </Button>
          <Button
            variant="primary"
            onClick={(e) => handleSubmit(e, true)}
            disabled={
              loading.create ||
              loading.update ||
              autoFillLoading ||
              loadingAgents
            }
          >
            {loading.create || loading.update
              ? "Saving..."
              : "Save & Setup Job"}
          </Button>
        </Modal.Footer>
      </Modal>

      <CustomToast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        variant={toastVariant}
        position="top-end"
        delay={4000}
        autohide={true}
      />

      <JobSetupModal
        show={showJobSetupModal}
        onHide={handleJobSetupClose}
        appointment={savedAppointment}
      />
    </>
  );
};

export default AppointmentModal;
